import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AuthDialog from '../AuthDialog';
import { useNotification } from '../../contexts/NotificationContext';

interface AuthGuardProps {
  children: React.ReactElement;
  message?: string; // Message to show in the auth dialog (if supported by your AuthDialog)
}

/**
 * Wraps an interactive element and ensures the user is authenticated before allowing the interaction.
 * If not authenticated, it prevents the default action and opens the AuthDialog.
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({ children, message }) => {
  const { isAuthenticated } = useAuth();
  const { showError } = useNotification();
  const [showAuthDialog, setShowAuthDialog] = useState(false); // Still keep for explicit triggers if needed

  const handleClick = (e: React.MouseEvent) => {
    console.log('[AuthGuard] Click intercepted. Authenticated:', isAuthenticated);
    
    if (!isAuthenticated) {
      console.log('[AuthGuard] User not authenticated, preventing default and showing error');
      e.preventDefault();
      e.stopPropagation();
      showError(message || 'Please sign in to manage your collection');
      return;
    }
    
    console.log('[AuthGuard] User authenticated, propagating click');
    // If authenticated, let the event propagate to the child's onClick
    if (children.props.onClick) {
      children.props.onClick(e);
    }
  };

  return (
    <>
      {React.cloneElement(children, { onClick: handleClick })}
      
      <AuthDialog 
        open={showAuthDialog} 
        onClose={() => setShowAuthDialog(false)}
        // We could pass the message here if AuthDialog supports it, 
        // e.g. initialView or a custom title prop
      />
    </>
  );
};

export default AuthGuard;
